import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePrefs } from "@/lib/context";

const Ctx = createContext({ ticket: null, start: () => {}, stop: () => {} });
export const useGpsShare = () => useContext(Ctx);
const MIN_GAP = 8000, HEARTBEAT = 30000;

// App-level sharing so the position keeps being sent while the provider navigates
export function GpsShareProvider({ children }) {
  const { t } = usePrefs();
  const [ticket, setTicket] = useState(null);
  const fix = useRef(null), sent = useRef(0);
  const tid = ticket?.id;
  useEffect(() => {
    if (!tid) return;
    if (!navigator.geolocation) { toast.error(t("gpsUnavailable")); setTicket(null); return; }
    let lock = null, pending = null;
    const wake = () => navigator.wakeLock?.request("screen").then((l) => { lock = l; }).catch(() => {});
    const onVis = () => document.visibilityState === "visible" && wake();
    wake(); document.addEventListener("visibilitychange", onVis);
    const send = () => {
      if (!fix.current) return;
      sent.current = Date.now();
      api.post(`/tickets/${tid}/location`, fix.current).catch((e) => {
        if (e.response?.status === 400 || e.response?.status === 403) setTicket(null);
      });
    };
    const watch = navigator.geolocation.watchPosition(
      (p) => {
        const { latitude: lat, longitude: lng, accuracy, speed } = p.coords;
        fix.current = { lat, lng, accuracy, speed };
        clearTimeout(pending);
        const wait = MIN_GAP - (Date.now() - sent.current);
        if (wait <= 0) send(); else pending = setTimeout(send, wait);
      },
      (e) => { if (e.code === 1) { toast.error(t("gpsDenied")); setTicket(null); } },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
    );
    const hb = setInterval(() => { if (Date.now() - sent.current >= HEARTBEAT) send(); }, 10000);
    toast.success(t("sharingActive"));
    return () => {
      navigator.geolocation.clearWatch(watch); clearTimeout(pending); clearInterval(hb);
      document.removeEventListener("visibilitychange", onVis); lock?.release().catch(() => {});
      fix.current = null; sent.current = 0;
    };
  }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Ctx.Provider value={{ ticket, start: (tk) => setTicket({ id: tk.id, number: tk.number }), stop: () => setTicket(null) }}>{children}</Ctx.Provider>;
}

export function GpsShareIndicator() {
  const { ticket, stop } = useGpsShare();
  const { t } = usePrefs();
  if (!ticket) return null;
  return (
    <div className="flex items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800" data-testid="gps-global-indicator">
      <span className="live-dot" />
      <Link to={`/tickets/${ticket.id}`} className="hidden hover:underline sm:inline">{ticket.number}</Link>
      <button onClick={stop} className="font-bold text-rose-700 hover:underline" data-testid="gps-global-stop">{t("stop")}</button>
    </div>
  );
}
